import options from '@/utils/options'

const PHENOL_IDS = options.waterPheonolIds
const PHENOL_PRIMARY_ID = 'WATER_TEST_027'
/** 직접법 — 시험항목 선택 UI에서는 추출법(027)만 노출하고, 토글은 활성 행 기준으로 처리 */
const PHENOL_SECONDARY_ID = 'WATER_TEST_102'
const PHENOL_DISPLAY_NAME = '페놀류'

/**
 * WATER 카테고리 전용 — testId가 페놀류(추출법/직접법)인지 판별
 * @param {string} testId
 * @param {string} [category] - 'WATER'일 때만 true 반환
 */
export function isPhenolTest(testId, category) {
	if (category && category !== 'WATER') return false
	return PHENOL_IDS.includes(testId)
}

/**
 * 문서/미리보기용 통합 이름 반환 ("페놀류")
 * 페놀류가 아니면 fallbackName을 그대로 반환
 */
export function getPhenolDisplayName(testId, fallbackName, category) {
	if (isPhenolTest(testId, category)) return PHENOL_DISPLAY_NAME
	return fallbackName
}

/**
 * 페놀류 항목의 기준 testId 반환 (항상 WATER_TEST_027)
 * 페놀류가 아니면 원래 testId를 그대로 반환
 */
export function getPhenolPrimaryId(testId, category) {
	if (isPhenolTest(testId, category)) return PHENOL_PRIMARY_ID
	return testId
}

/**
 * 동일 라벨(페놀류)로 매칭된 헤더가 여러 개일 때, 시료 testList에서 실제 활성(isTest)인 페놀 행에 대응하는 헤더를 반환.
 * 활성 페놀이 없으면(전부 OFF) 추출법(027) 헤더를 기본으로 사용.
 * @param {Array} testList
 * @param {Array<{ testId: string }>} matchingHeaders - 같은 label을 가진 headers
 * @returns {object|null}
 */
export function resolvePhenolHeaderForToggle(testList, matchingHeaders) {
	if (!Array.isArray(matchingHeaders) || matchingHeaders.length === 0) return null
	const phenolHeaders = matchingHeaders.filter(h => PHENOL_IDS.includes(h.testId))
	if (phenolHeaders.length === 0) return null
	const active = testList?.find(t => PHENOL_IDS.includes(t.testId) && t.isTest)
	if (active) {
		return phenolHeaders.find(h => h.testId === active.testId) || phenolHeaders[0]
	}
	return phenolHeaders.find(h => h.testId === PHENOL_PRIMARY_ID) || phenolHeaders[0]
}

/**
 * 카테고리 이름 배열에서 페놀류 중복 제거
 * 추출법/직접법 이름이 각각 들어와도 "페놀류" 하나로 치환 후 중복 제거
 * @param {string[]} categories - 카테고리 이름 배열
 * @param {Array} testCategoryList - testCategory.list (testId, lab_name, name 등 포함)
 * @param {string} [category] - 'WATER'일 때만 동작
 * @returns {string[]}
 */
export function deduplicatePhenolCategories(categories, testCategoryList, category) {
	if (category !== 'WATER') return categories

	const phenolLabels = new Set()
	testCategoryList.forEach(el => {
		if (PHENOL_IDS.includes(el.testId)) {
			if (el.lab_name) phenolLabels.add(el.lab_name)
			if (el.name) phenolLabels.add(el.name)
			if (el.search_name) phenolLabels.add(el.search_name)
			if (el.original_name) phenolLabels.add(el.original_name)
		}
	})

	const result = []
	let phenolAdded = false
	categories.forEach(c => {
		if (phenolLabels.has(c)) {
			if (!phenolAdded) {
				result.push(PHENOL_DISPLAY_NAME)
				phenolAdded = true
			}
		} else {
			result.push(c)
		}
	})
	return result
}

// --- 페놀류(027 추출법 / 102 직접법) 공통 병합 ---
// ModalResult(docx), 측정기록부·시험결과(Report), 엑셀 등에서 동일 규칙 사용

const phenolSampleKey = (s) => s.no || s.name || ''

/**
 * 시료 목록 testResult에서 isTest=true 인 testId 집합 (ModalResult docx.setTestResult 와 동일)
 * @param {Array} sampleList
 * @returns {Set<string>}
 */
export function collectPhenolShowIds(sampleList) {
	const showIdsSet = new Set()
	if (!Array.isArray(sampleList)) return showIdsSet
	sampleList.forEach((sample) => {
		if (!sample?.testResult) return
		Object.entries(sample.testResult).forEach(([key, testInfo]) => {
			if (testInfo?.isTest) showIdsSet.add(key)
		})
	})
	return showIdsSet
}

/**
 * 페놀 시험일지 중 createDatetime 기준 최신 testId (027 또는 102)
 * @param {Array} reportList - api.project.getReport().list
 * @returns {string|null}
 */
export function pickLatestPhenolTestId(reportList) {
	const phenolReports = (reportList || []).filter((r) => isPhenolTest(r.testId))
	if (phenolReports.length === 0) return null
	const latest = phenolReports.reduce((a, b) =>
		(new Date(a.createDatetime) > new Date(b.createDatetime) ? a : b)
	)
	return latest.testId
}

/**
 * 페놀류 시료별 측정값 병합 (docx.setTestResult 3-1 과 동일)
 * - 전 시료 직접법(102)만 활성 + 최신 일지가 102 → 102 목록 전체 사용
 * - 그 외 → 027 우선, 빈 값만 102로 보충
 */
export function mergePhenolSampleLists(primaryList, secondaryList, options = {}) {
	const { latestPhenolTestId = null, showIds = null } = options
	if (!secondaryList?.length) return primaryList ? primaryList.map((s) => ({ ...s })) : []
	if (!primaryList?.length) return secondaryList.map((s) => ({ ...s }))

	const showIdSet = showIds instanceof Set ? showIds : new Set(showIds || [])
	// 전 시료가 직접법(102)만 켜져 있고, 최신 일지도 102일 때
	const allOnSecondary =
		latestPhenolTestId &&
		latestPhenolTestId !== PHENOL_PRIMARY_ID &&
		!showIdSet.has(PHENOL_PRIMARY_ID)

	if (allOnSecondary) {
		return secondaryList.map((s) => ({ ...s }))
	}

	// 추출법(027) 값 우선, '-'·빈 값만 직접법(102)로 채움
	const merged = new Map()
	primaryList.forEach((s) => merged.set(phenolSampleKey(s), { ...s }))
	secondaryList.forEach((s) => {
		const key = phenolSampleKey(s)
		const existing = merged.get(key)
		if (existing) {
			if ((!existing.value || existing.value === '-') && s.value && s.value !== '-') {
				existing.value = s.value
			}
		} else {
			merged.set(key, { ...s })
		}
	})
	return Array.from(merged.values())
}

/**
 * 페놀류 관련기준(benchmark) — 활성 isTest 우선, 없으면 반대편 fallback
 * @param {Object} testResult - 시료 testResult 맵
 * @returns {Object|null}
 */
export function resolvePhenolBenchmark(testResult) {
	if (!testResult) return null

	const bench027 = testResult[PHENOL_PRIMARY_ID]?.benchmark
	const bench102 = testResult[PHENOL_SECONDARY_ID]?.benchmark
	const is027Active = !!testResult[PHENOL_PRIMARY_ID]?.isTest
	const is102Active = !!testResult[PHENOL_SECONDARY_ID]?.isTest

	const hasBench027 = bench027 && typeof bench027 === 'object' && Object.keys(bench027).length > 0
	const hasBench102 = bench102 && typeof bench102 === 'object' && Object.keys(bench102).length > 0

	// 켜진 시험방법의 기준 우선
	if (is027Active && hasBench027) return bench027
	if (is102Active && hasBench102) return bench102
	// 102만 활성인데 기준은 027에만 있는 경우 등
	if (!hasBench027 && hasBench102) return bench102
	if (hasBench027) return bench027
	return hasBench102 ? bench102 : null
}

/** 비페놀은 해당 testId benchmark, 페놀류(027 행)는 resolvePhenolBenchmark */
export function resolvePhenolBenchmarkForTest(testId, testResult) {
	if (!testResult) return null
	if (testId !== PHENOL_PRIMARY_ID) return testResult[testId]?.benchmark ?? null
	return resolvePhenolBenchmark(testResult)
}

/**
 * category 배열(시험항목 목록)에서 페놀류 027/102를 하나로 병합
 * 102 항목은 제거하고, 027 항목에 활성 시험방법 기준 시료별 결과를 병합
 * @param {Array} categoryList
 * @param {string} [type]
 * @param {{ latestPhenolTestId?: string|null, showIds?: Set|Array }} [options]
 * @returns {Array}
 */
export function mergePhenolCategoryRows(categoryList, type, options = {}) {
	if (type !== 'WATER') return categoryList

	const { latestPhenolTestId = null, showIds = null } = options
	const primaryItem = categoryList.find((el) => el.testId === PHENOL_PRIMARY_ID)
	const secondaryItem = categoryList.find(
		(el) => isPhenolTest(el.testId) && el.testId !== PHENOL_PRIMARY_ID
	)

	if (!primaryItem || !secondaryItem) return categoryList

	const showIdSet = showIds instanceof Set ? showIds : new Set(showIds || [])
	const allOnSecondary =
		latestPhenolTestId &&
		latestPhenolTestId !== PHENOL_PRIMARY_ID &&
		!showIdSet.has(PHENOL_PRIMARY_ID)

	const mergeOpts = { latestPhenolTestId, showIds: showIdSet }

	if (secondaryItem.list || primaryItem.list) {
		primaryItem.list = mergePhenolSampleLists(
			primaryItem.list,
			secondaryItem.list,
			mergeOpts
		)
	}

	// 직접법만 쓸 때 분석방법·단위도 102 쪽으로 맞춤 나중에 달라질수도있어서
	if (allOnSecondary) {
		;['method', 'machine', 'unit', 'report_method'].forEach((key) => {
			if (secondaryItem[key] != null && secondaryItem[key] !== '') {
				primaryItem[key] = secondaryItem[key]
			}
		})
	}

	// 화면에는 페놀류(027) 한 행만 남김
	return categoryList.filter((el) => el.testId !== secondaryItem.testId)
}

export { PHENOL_IDS, PHENOL_PRIMARY_ID, PHENOL_SECONDARY_ID, PHENOL_DISPLAY_NAME }
